
import React, { useState, useEffect } from 'react';
import { ScheduleEntry, SchoolSettings, UserRole, Student, AgendaEntry, AttendanceRecord } from '../types';
import { Clock, MapPin, Plus, Trash2, X, AlertTriangle, Info, ShieldAlert, GripVertical, CalendarDays, Upload, FileText, Loader2, ExternalLink, Save, CheckCircle, RefreshCw, Printer, FileSpreadsheet, GraduationCap, Users, Award, BookOpen, Search, ChevronDown, ChevronUp, Link as LinkIcon, ClipboardList, UserCheck, Coffee } from 'lucide-react';
import { DndContext, useDraggable, useDroppable, DragEndEvent, DragOverlay, defaultDropAnimationSideEffects } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { db } from '../src/lib/firebase';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface Props {
  schedules: ScheduleEntry[];
  setSchedules: (schedules: ScheduleEntry[]) => void;
  availableClasses: string[];
  teacherSubject: string;
  schoolSettings: SchoolSettings;
  userRole: UserRole;
  onUpdateSettings: (settings: SchoolSettings) => Promise<any>;
  activeUser?: any;
  students?: Student[];
  agendas?: AgendaEntry[];
  attendanceRecords?: AttendanceRecord[];
}

const DraggableEntry: React.FC<{ entry: ScheduleEntry; colorTheme: any; onDelete: (id: string) => void; periodTime?: { startTime: string; endTime: string } }> = ({ entry, colorTheme, onDelete, periodTime }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: entry.id,
    data: { entry }
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 100 : 1,
  };

  const isBreak = entry.isBreak || entry.subject === 'Jam Istirahat';

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners}
      className={`group p-4 ${
        isBreak 
          ? 'bg-emerald-50/70 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/40 text-emerald-900 dark:text-emerald-200' 
          : `${colorTheme.bg} ${colorTheme.border} ${colorTheme.text || 'text-white'}`
      } border rounded-2xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-grab active:cursor-grabbing relative text-left`}
    >
       <div className="flex justify-between items-start gap-2 mb-2.5">
         <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <GripVertical size={12} className={isBreak ? 'text-emerald-500' : 'text-white/80'} />
              <span className={`text-[10px] font-black px-2 py-0.5 rounded-lg uppercase tracking-wider ${
                isBreak 
                  ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300' 
                  : colorTheme.accent
              }`}>
                JAM {entry.period}
              </span>
            </div>
            {periodTime && (
              <span className={`text-[11px] font-bold flex items-center gap-1 ${
                isBreak ? 'text-emerald-700/80 dark:text-emerald-400/80' : 'text-white/80'
              }`}>
                <Clock className="w-3.5 h-3.5 opacity-70" /> {periodTime.startTime} - {periodTime.endTime}
              </span>
            )}
         </div>
         <button 
           onPointerDown={e => e.stopPropagation()} 
           onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }} 
           className={`p-1.5 rounded-xl transition-all opacity-0 group-hover:opacity-100 ${
             isBreak 
               ? 'text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 bg-slate-100/50 dark:bg-slate-800/50 hover:bg-rose-50 dark:hover:bg-rose-950/30' 
               : 'text-white/70 hover:text-white bg-white/15 hover:bg-white/25'
           }`}
         >
           <Trash2 size={13} />
         </button>
       </div>
       <p className={`text-xs font-black leading-tight mb-2.5 truncate ${
         isBreak ? 'text-emerald-850 dark:text-emerald-100' : 'text-white font-black'
       }`}>
         {isBreak ? '☕ JAM ISTIRAHAT' : entry.subject}
       </p>
       {!isBreak && (
         <div className="flex items-center gap-1 text-[11px] font-black text-white bg-white/15 border border-white/10 px-2 py-0.5 rounded-lg w-fit shadow-2xs">
           <MapPin size={10} className="text-white" />
           <span>Kelas {entry.class}</span>
         </div>
       )}
    </div>
  );
};

const DroppableSlot: React.FC<{ day: string; period: number; children: React.ReactNode }> = ({ day, period, children }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `slot-${day}-${period}`,
    data: { day, period }
  });

  return (
    <div 
      ref={setNodeRef} 
      className={`relative group transition-all rounded-2xl ${isOver ? 'ring-4 ring-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/10 scale-105 z-10' : ''}`}
    >
      {children}
    </div>
  );
};

const ScheduleView: React.FC<Props> = ({ schedules, setSchedules, availableClasses, teacherSubject, schoolSettings, userRole, onUpdateSettings, activeUser, students = [], agendas = [], attendanceRecords = [] }) => {
  const [activeTab, setActiveTab] = useState<'jadwal' | 'kalender' | 'periode'>(userRole === UserRole.Admin ? 'kalender' : 'jadwal');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; id: string }>({ isOpen: false, id: '' });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Form Year/Semester Edit state
  const [academicYear, setAcademicYear] = useState(schoolSettings.academicYear || '');
  const [semester, setSemester] = useState(schoolSettings.semester || '');
  const [isSavingPeriod, setIsSavingPeriod] = useState(false);
  const [isSavedPeriod, setIsSavedPeriod] = useState(false);

  // Transition Period Admin Wizard States
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionStep, setTransitionStep] = useState(1);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  
  const [targetYear, setTargetYear] = useState('');
  const [targetSemester, setTargetSemester] = useState('1 (Ganjil)');
  const [shouldPromoteStudents, setShouldPromoteStudents] = useState(true);
  const [clearAttendance, setClearAttendance] = useState(false);
  const [clearGrades, setClearGrades] = useState(false);
  const [clearAgendas, setClearAgendas] = useState(false);
  const [clearAssignments, setClearAssignments] = useState(false);

  // Detail View State for Grade 9 & Alumni Transisi
  const [viewTransitionDetail, setViewTransitionDetail] = useState<'siswa' | 'agenda'>('siswa');
  const [transitionClassFilter, setTransitionClassFilter] = useState<string>('SEMUA');
  const [transitionSearch, setTransitionSearch] = useState<string>('');

  // Grade 9 & Alumni Students list
  const transitionStudentsList = React.useMemo(() => {
    return (students || []).filter(s => {
      const cls = (s.class || '').trim();
      const status = s.status || 'Aktif';
      const isGrade9 = status === 'Aktif' && (cls.startsWith('9') || cls.match(/^IX/i));
      const isAlumni = status === 'Tidak Aktif' || cls.toUpperCase() === 'ALUMNI';
      return isGrade9 || isAlumni;
    });
  }, [students]);

  // Grade 9 & Alumni Agendas list
  const transitionAgendasList = React.useMemo(() => {
    return (agendas || []).filter(a => {
      const cls = (a.class || '').trim();
      const isGrade9 = cls.startsWith('9') || cls.match(/^IX/i);
      const isAlumni = cls.toUpperCase() === 'ALUMNI';
      return isGrade9 || isAlumni;
    });
  }, [agendas]);

  // Filtered students for Transisi card
  const filteredTransitionStudents = React.useMemo(() => {
    return transitionStudentsList.filter(s => {
      const cls = (s.class || '').trim();
      const status = s.status || 'Aktif';
      const matchesClass = 
        transitionClassFilter === 'SEMUA' ? true :
        transitionClassFilter === 'ALUMNI' ? (status === 'Tidak Aktif' || cls.toUpperCase() === 'ALUMNI') :
        cls === transitionClassFilter;
      
      const search = transitionSearch.toLowerCase().trim();
      const matchesSearch = !search || 
        (s.name || '').toLowerCase().includes(search) || 
        (s.nisn || '').toLowerCase().includes(search) ||
        (s.nis || '').toLowerCase().includes(search) ||
        cls.toLowerCase().includes(search);

      return matchesClass && matchesSearch;
    });
  }, [transitionStudentsList, transitionClassFilter, transitionSearch]);

  // Filtered agendas for Transisi card
  const filteredTransitionAgendas = React.useMemo(() => {
    return transitionAgendasList.filter(a => {
      const cls = (a.class || '').trim();
      const matchesClass = 
        transitionClassFilter === 'SEMUA' ? true :
        transitionClassFilter === 'ALUMNI' ? cls.toUpperCase() === 'ALUMNI' :
        cls === transitionClassFilter;

      const search = transitionSearch.toLowerCase().trim();
      const matchesSearch = !search ||
        (a.topic || '').toLowerCase().includes(search) ||
        (a.subject || '').toLowerCase().includes(search) ||
        (a.teacherName || '').toLowerCase().includes(search) ||
        (a.tpDescription || '').toLowerCase().includes(search) ||
        (a.notes || '').toLowerCase().includes(search) ||
        cls.toLowerCase().includes(search);

      return matchesClass && matchesSearch;
    });
  }, [transitionAgendasList, transitionClassFilter, transitionSearch]);

  // Calculation for Grade 9 / Graduating classes & Alumni
  const grade9ClassesList = React.useMemo(() => {
    const grade9ClassMap: Record<string, number> = {};

    (students || []).forEach(s => {
      const cls = (s.class || '').trim();
      const status = s.status || 'Aktif';
      if (status === 'Aktif' && (cls.startsWith('9') || cls.match(/^IX/i))) {
        grade9ClassMap[cls] = (grade9ClassMap[cls] || 0) + 1;
      }
    });

    // Also include grade 9 classes from schoolSettings.classes if any are empty
    (schoolSettings.classes || []).forEach(cls => {
      const trimmed = cls.trim();
      if ((trimmed.startsWith('9') || trimmed.match(/^IX/i)) && !(trimmed in grade9ClassMap)) {
        grade9ClassMap[trimmed] = 0;
      }
    });

    return Object.entries(grade9ClassMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [students, schoolSettings.classes]);

  const grade9StudentsCount = React.useMemo(() => {
    return grade9ClassesList.reduce((acc, curr) => acc + curr.count, 0);
  }, [grade9ClassesList]);

  const alumniStudentsCount = React.useMemo(() => {
    return (students || []).filter(s => s.status === 'Tidak Aktif' || (s.class || '').toUpperCase() === 'ALUMNI').length;
  }, [students]);

  // Sync Year/Semester from props
  useEffect(() => {
    setAcademicYear(schoolSettings.academicYear || '');
    setSemester(schoolSettings.semester || '');
  }, [schoolSettings]);

  const handlePrintSchedule = () => {
    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Mohon izinkan pop-up untuk mencetak.');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cetak Jadwal Mengajar</title>
        <style>
          body { font-family: 'Times New Roman', Times, serif; padding: 20px; color: #000; }
          .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
          .kop-logo { width: 80px; height: 80px; object-fit: contain; }
          .kop-text { flex: 1; text-align: center; }
          .kop-text p { margin: 0; line-height: 1.2; }
          .kop-gov { font-size: 16px; font-weight: bold; text-transform: uppercase; }
          .kop-dept { font-size: 16px; font-weight: bold; text-transform: uppercase; }
          .kop-school { font-size: 24px; font-weight: bold; text-transform: uppercase; }
          .kop-address { font-size: 12px; font-style: italic; }
          .kop-additional { font-size: 12px; }
          .kop-line-1 { height: 3px; background-color: #000; width: 100%; margin-top: 10px; }
          .kop-line-2 { height: 1px; background-color: #000; width: 100%; margin-top: 2px; }
          
          .report-title { text-align: center; font-size: 16px; font-weight: bold; margin: 20px 0; text-transform: uppercase; }
          
          .meta-table { width: 100%; margin-bottom: 20px; font-size: 12px; }
          .meta-table td { padding: 4px 5px; vertical-align: top; }
          .meta-table td:first-child { width: 120px; }
          .meta-table td:nth-child(2) { width: 10px; }
          
          .data-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 30px; }
          .data-table th, .data-table td { border: 1px solid #000; padding: 8px 6px; text-align: center; }
          table.data-table th { background-color: #f1f5f9; font-weight: bold; }
          .day-header { background-color: #e2e8f0; font-weight: bold; text-align: left; padding-left: 10px; }
          
          .signature-container { width: 100%; display: flex; justify-content: space-between; margin-top: 40px; }
          .signature-box { width: 250px; text-align: center; font-size: 12px; }
          .signature-space { height: 60px; }
          .signature-name { font-weight: bold; text-decoration: underline; }
          
          @media print {
            @page { size: portrait; margin: 2cm; }
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <!-- Kop Surat -->
        <div class="kop-container">
          <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
          <div class="kop-text">
            <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
            <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
            <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
            <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
            ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
          </div>
          <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
        </div>
        <div class="kop-line-1"></div>
        <div class="kop-line-2"></div>

        <div class="report-title">JADWAL MENGAJAR GURU</div>

        <table class="meta-table">
          <tr>
            <td>Nama Guru</td><td>:</td><td><strong>${activeUser?.name || 'Guru Mata Pelajaran'}</strong></td>
          </tr>
          <tr>
            <td>NIP</td><td>:</td><td>${activeUser?.nip || '-'}</td>
          </tr>
          <tr>
            <td>Mata Pelajaran</td><td>:</td><td>${teacherSubject}</td>
          </tr>
          <tr>
            <td>Tahun Pelajaran</td><td>:</td><td>${schoolSettings.academicYear || '-'} (${schoolSettings.semester || '-'})</td>
          </tr>
        </table>

        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 25%;">Hari</th>
              <th style="width: 25%;">Jam Ke</th>
              <th style="width: 25%;">Waktu</th>
              <th style="width: 25%;">Kelas / Rombel</th>
            </tr>
          </thead>
          <tbody>
            ${days.map(day => {
              const daySchedules = schedules.filter(s => s.day === day).sort((a, b) => a.period - b.period);
              if (daySchedules.length === 0) {
                return `
                  <tr>
                    <td style="font-weight: bold; text-align: left; padding-left: 10px;">${day}</td>
                    <td colspan="3" style="font-style: italic; color: #666;">Tidak ada jadwal mengajar</td>
                  </tr>
                `;
              }
              return daySchedules.map((s, idx) => {
                const pt = getPeriodTime(s.period, day);
                const timeStr = pt ? `${pt.startTime} - ${pt.endTime}` : '-';
                return `
                  <tr>
                    ${idx === 0 ? `<td rowspan="${daySchedules.length}" style="font-weight: bold; vertical-align: middle;">${day}</td>` : ''}
                    <td>Jam Ke-${s.period}</td>
                    <td>${timeStr}</td>
                    <td style="font-weight: bold;">${s.class}</td>
                  </tr>
                `;
              }).join('');
            }).join('')}
          </tbody>
        </table>

        <div class="signature-container">
          <div class="signature-box" style="text-align: left;">
            <p>Mengetahui,</p>
            <p>Kepala Sekolah</p>
            <div class="signature-space"></div>
            <p class="signature-name">${schoolSettings.principalName || 'Kepala Sekolah'}</p>
            <p>NIP. ${schoolSettings.principalNip || '-'}</p>
          </div>
          <div class="signature-box">
            <p>Kawali, ${today}</p>
            <p>Guru Mata Pelajaran</p>
            <div class="signature-space"></div>
            <p class="signature-name">${activeUser?.name || 'Guru Mata Pelajaran'}</p>
            <p>NIP. ${activeUser?.nip || '-'}</p>
          </div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Jadwal_Mengajar');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    // Set column widths
    worksheet.columns = [
      { width: 15 },  // A: Hari
      { width: 15 },  // B: Jam Ke
      { width: 25 },  // C: Waktu
      { width: 20 }   // D: Kelas / Rombel
    ];

    // Add Header (Kop)
    worksheet.mergeCells('A1:D1');
    worksheet.getCell('A1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('A1').font = { bold: true, size: 12 };
    worksheet.getCell('A1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A2:D2');
    worksheet.getCell('A2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('A2').font = { bold: true, size: 12 };
    worksheet.getCell('A2').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A3:D3');
    worksheet.getCell('A3').value = schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI';
    worksheet.getCell('A3').font = { bold: true, size: 14 };
    worksheet.getCell('A3').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A4:D4');
    worksheet.getCell('A4').value = schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253';
    worksheet.getCell('A4').font = { italic: true, size: 10 };
    worksheet.getCell('A4').alignment = { horizontal: 'center' };

    worksheet.addRow([]); // Row 5 empty

    worksheet.mergeCells('A6:D6');
    worksheet.getCell('A6').value = 'JADWAL MENGAJAR GURU';
    worksheet.getCell('A6').font = { bold: true, size: 12 };
    worksheet.getCell('A6').alignment = { horizontal: 'center' };

    worksheet.addRow([]); // Row 7 empty

    worksheet.getCell('A8').value = 'Nama Guru:';
    worksheet.getCell('A8').font = { bold: true };
    worksheet.getCell('B8').value = activeUser?.name || 'Guru Mata Pelajaran';

    worksheet.getCell('A9').value = 'NIP:';
    worksheet.getCell('A9').font = { bold: true };
    worksheet.getCell('B9').value = activeUser?.nip || '-';

    worksheet.getCell('A10').value = 'Mata Pelajaran:';
    worksheet.getCell('A10').font = { bold: true };
    worksheet.getCell('B10').value = teacherSubject;

    worksheet.getCell('A11').value = 'Tahun Pelajaran:';
    worksheet.getCell('A11').font = { bold: true };
    worksheet.getCell('B11').value = `${schoolSettings.academicYear || '-'} (${schoolSettings.semester || '-'})`;

    worksheet.addRow([]); // Row 12 empty

    // Table Header
    const headerRow = worksheet.addRow(['Hari', 'Jam Ke', 'Waktu', 'Kelas / Rombel']);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Populate data
    days.forEach(day => {
      const daySchedules = schedules.filter(s => s.day === day).sort((a, b) => a.period - b.period);
      if (daySchedules.length === 0) {
        const row = worksheet.addRow([day, 'Tidak ada jadwal mengajar', '-', '-']);
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
        worksheet.mergeCells(`B${row.number}:D${row.number}`);
      } else {
        const startRowNumber = worksheet.lastRow!.number + 1;
        daySchedules.forEach((s, idx) => {
          const pt = getPeriodTime(s.period, day);
          const timeStr = pt ? `${pt.startTime} - ${pt.endTime}` : '-';
          const row = worksheet.addRow([
            day,
            `Jam Ke-${s.period}`,
            timeStr,
            s.class
          ]);
          row.eachCell((cell, colNumber) => {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'center'
            };
            if (colNumber === 4) {
              cell.font = { bold: true };
            }
          });
        });
        const endRowNumber = worksheet.lastRow!.number;
        if (endRowNumber > startRowNumber) {
          worksheet.mergeCells(`A${startRowNumber}:A${endRowNumber}`);
          worksheet.getCell(`A${startRowNumber}`).alignment = { vertical: 'middle', horizontal: 'center' };
        }
      }
    });

    // Add empty row after data
    worksheet.addRow([]);
    worksheet.addRow([]);

    const signRowIndex = worksheet.lastRow!.number + 1;
    
    // Signatures
    worksheet.getCell(`A${signRowIndex}`).value = 'Mengetahui,';
    worksheet.getCell(`A${signRowIndex + 1}`).value = 'Kepala Sekolah';
    worksheet.getCell(`A${signRowIndex + 5}`).value = schoolSettings.principalName || 'Kepala Sekolah';
    worksheet.getCell(`A${signRowIndex + 5}`).font = { bold: true, underline: true };
    worksheet.getCell(`A${signRowIndex + 6}`).value = `NIP. ${schoolSettings.principalNip || '-'}`;

    const todayStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    worksheet.getCell(`D${signRowIndex}`).value = `Kawali, ${todayStr}`;
    worksheet.getCell(`D${signRowIndex + 1}`).value = 'Guru Mata Pelajaran';
    worksheet.getCell(`D${signRowIndex + 5}`).value = activeUser?.name || 'Guru Mata Pelajaran';
    worksheet.getCell(`D${signRowIndex + 5}`).font = { bold: true, underline: true };
    worksheet.getCell(`D${signRowIndex + 6}`).value = `NIP. ${activeUser?.nip || '-'}`;

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = `Jadwal_Mengajar_${(activeUser?.name || 'Guru').replace(/\s+/g, '_')}.xlsx`;
    saveAs(new Blob([buffer]), fileName);
  };

  // Calculate next academic period
  const calculateNextPeriod = (currentYear: string, currentSemester: string) => {
    let nextYear = currentYear;
    let nextSemester = '1 (Ganjil)';

    if (currentSemester.includes('1')) {
      nextSemester = '2 (Genap)';
    } else {
      nextSemester = '1 (Ganjil)';
      const match = currentYear.match(/^(\d{4})\/(\d{4})$/);
      if (match) {
        const start = parseInt(match[1], 10) + 1;
        const end = parseInt(match[2], 10) + 1;
        nextYear = `${start}/${end}`;
      } else {
        const nextStartYear = new Date().getFullYear();
        nextYear = `${nextStartYear}/${nextStartYear + 1}`;
      }
    }
    return { nextYear, nextSemester };
  };

  const openTransitionWizard = () => {
    const currentYear = schoolSettings.academicYear || '2025/2026';
    const currentSemester = schoolSettings.semester || '2 (Genap)';
    const next = calculateNextPeriod(currentYear, currentSemester);
    setTargetYear(next.nextYear);
    setTargetSemester(next.nextSemester);
    setShouldPromoteStudents(next.nextSemester.includes('1'));
    setClearAttendance(true);
    setClearGrades(true);
    setClearAgendas(true);
    setClearAssignments(true);
    setTransitionStep(1);
    setIsTransitioning(false);
    setTransitionProgress(0);
    setTransitionError(null);
    setShowTransitionModal(true);
  };

  const handleSavePeriodOnly = async () => {
    setIsSavingPeriod(true);
    try {
      await onUpdateSettings({
        ...schoolSettings,
        academicYear,
        semester
      });
      setIsSavedPeriod(true);
      setTimeout(() => setIsSavedPeriod(false), 3000);
    } catch (err) {
      console.error("Gagal menyimpan periode:", err);
      alert("Gagal menyimpan periode akademik");
    } finally {
      setIsSavingPeriod(false);
    }
  };

  const executeTransition = async () => {
    setIsTransitioning(true);
    setTransitionError(null);
    setTransitionProgress(5);
    try {
      const batchList: any[] = [];
      let currentBatch = writeBatch(db);
      let operationCount = 0;

      const addOperationToBatch = (ref: any, data: any, isDelete = false) => {
        if (isDelete) {
          currentBatch.delete(ref);
        } else {
          currentBatch.set(ref, data, { merge: true });
        }
        operationCount++;
        if (operationCount >= 400) {
          batchList.push(currentBatch);
          currentBatch = writeBatch(db);
          operationCount = 0;
        }
      };

      // 1. Promote Students
      if (shouldPromoteStudents) {
        setTransitionProgress(15);
        const studentsSnapshot = await getDocs(collection(db, 'students'));
        let count = 0;
        studentsSnapshot.forEach((sDoc) => {
          const student = sDoc.data();
          const currentClass = student.class || '';
          const match = currentClass.match(/^(\d+)(.*)$/);
          if (match) {
            const gradeNum = parseInt(match[1], 10);
            const suffix = match[2];
            let updatedClass = currentClass;
            let updatedStatus = student.status || 'Aktif';
            if (gradeNum === 9) {
              updatedClass = 'ALUMNI';
              updatedStatus = 'Tidak Aktif';
            } else {
              updatedClass = `${gradeNum + 1}${suffix}`;
            }

            const ref = doc(db, 'students', sDoc.id);
            addOperationToBatch(ref, {
              class: updatedClass,
              status: updatedStatus,
              updatedAt: new Date().toISOString()
            });
            count++;
          }
        });
        console.log(`Promoted ${count} students`);
      }

      // 2. Clear Attendance
      if (clearAttendance) {
        setTransitionProgress(35);
        const snapshot = await getDocs(collection(db, 'attendanceRecords'));
        snapshot.forEach((snapDoc) => {
          addOperationToBatch(doc(db, 'attendanceRecords', snapDoc.id), null, true);
        });
      }

      // 3. Clear Grades
      if (clearGrades) {
        setTransitionProgress(55);
        const assessmentSnapshot = await getDocs(collection(db, 'assessmentGrades'));
        assessmentSnapshot.forEach((snapDoc) => {
          addOperationToBatch(doc(db, 'assessmentGrades', snapDoc.id), null, true);
        });

        const sasSnapshot = await getDocs(collection(db, 'sasGrades'));
        sasSnapshot.forEach((snapDoc) => {
          addOperationToBatch(doc(db, 'sasGrades', snapDoc.id), null, true);
        });

        const formativeSnapshot = await getDocs(collection(db, 'formativeGrades'));
        formativeSnapshot.forEach((snapDoc) => {
          addOperationToBatch(doc(db, 'formativeGrades', snapDoc.id), null, true);
        });
      }

      // 4. Archive Agendas
      if (clearAgendas) {
        setTransitionProgress(75);
        const snapshot = await getDocs(collection(db, 'agendas'));
        snapshot.forEach((snapDoc) => {
          addOperationToBatch(doc(db, 'agendas', snapDoc.id), { isArchived: true });
        });
      }

      // 5. Clear Assignments and submissions
      if (clearAssignments) {
        setTransitionProgress(85);
        const tugasSnap = await getDocs(collection(db, 'tugas'));
        tugasSnap.forEach((snapDoc) => {
          addOperationToBatch(doc(db, 'tugas', snapDoc.id), null, true);
        });

        const submissionSnap = await getDocs(collection(db, 'tugasSubmissions'));
        submissionSnap.forEach((snapDoc) => {
          addOperationToBatch(doc(db, 'tugasSubmissions', snapDoc.id), null, true);
        });
      }

      // 6. Commit all batch operations
      setTransitionProgress(90);
      if (operationCount > 0) {
        batchList.push(currentBatch);
      }

      for (let i = 0; i < batchList.length; i++) {
        await batchList[i].commit();
        setTransitionProgress(90 + Math.floor((i + 1) / batchList.length * 5));
      }

      // 7. Finally update School Settings
      const updatedSettings = {
        ...schoolSettings,
        academicYear: targetYear,
        semester: targetSemester
      };
      
      await onUpdateSettings(updatedSettings);

      setTransitionProgress(100);
      setTransitionStep(3);
    } catch (err: any) {
      console.error("Transisi gagal:", err);
      setTransitionError(err?.message || "Terjadi kesalahan saat memproses transisi.");
      setIsTransitioning(false);
    }
  };
  
  const [newEntry, setNewEntry] = useState({
    day: 'Senin' as ScheduleEntry['day'],
    startPeriod: 1,
    endPeriod: 1,
    subject: teacherSubject,
    class: availableClasses[0] || ''
  });

  const getPeriodTime = (p: number, day?: string) => {
    if (day && schoolSettings.dayPeriodTimes?.[day]) {
      return schoolSettings.dayPeriodTimes[day].find((pt: any) => pt.period === p);
    }
    return schoolSettings.periodTimes?.find(pt => pt.period === p);
  };

  // Sync class selection if availableClasses changes
  useEffect(() => {
    if (availableClasses.length > 0 && !newEntry.class) {
      setNewEntry(prev => ({ ...prev, class: availableClasses[0] }));
    }
  }, [availableClasses, newEntry.class]);

  const days: ScheduleEntry['day'][] = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat'];
  const periods = Array.from({ length: 10 }, (_, i) => i + 1);

  const handleStartPeriodChange = (val: number) => {
    setNewEntry(prev => ({
      ...prev,
      startPeriod: val,
      endPeriod: val > prev.endPeriod ? val : prev.endPeriod
    }));
  };

  const handleEndPeriodChange = (val: number) => {
    if (val < newEntry.startPeriod) {
      alert("Jam selesai tidak boleh lebih awal dari jam mulai!");
      return;
    }
    setNewEntry(prev => ({ ...prev, endPeriod: val }));
  };

  const handleAdd = () => {
    const { day, startPeriod, endPeriod, class: cls } = newEntry;

    if (!cls) {
        alert("Pilih Rombel (Kelas) terlebih dahulu.");
        return;
    }

    if (day && startPeriod && endPeriod) {
      if (startPeriod > endPeriod) {
        alert("Logika Jam Tidak Valid: Jam mulai tidak boleh lebih besar dari jam selesai.");
        return;
      }

      const newEntries: ScheduleEntry[] = [];
      const conflicts: number[] = [];

      for (let p = startPeriod; p <= endPeriod; p++) {
        if (schedules.some(s => s.day === day && s.period === p)) {
          conflicts.push(p);
        } else {
          newEntries.push({
            id: `sch${Date.now()}-${p}`,
            day: day,
            period: p,
            time: '',
            subject: teacherSubject,
            class: cls
          });
        }
      }

      if (conflicts.length > 0) {
        alert(`Konflik Jadwal: Jam ke-${conflicts.join(', ')} pada hari ${day} sudah terisi jadwal lain!`);
        return;
      }

      setSchedules([...schedules, ...newEntries]);
      setIsModalOpen(false);
      
      setNewEntry({ 
        ...newEntry, 
        startPeriod: 1, 
        endPeriod: 1, 
        day: 'Senin',
        class: availableClasses[0] || ''
      });
    }
  };

  const [isBreakModalOpen, setIsBreakModalOpen] = useState(false);
  const [breakEntry, setBreakEntry] = useState({
    day: 'Senin' as ScheduleEntry['day'],
    period: 4.5
  });

  const handleAddBreak = () => {
    const { day, period } = breakEntry;
    const existingBreaks = schedules.filter(s => s.day === day && (s.isBreak || s.subject === 'Jam Istirahat'));
    if (existingBreaks.length >= 2) {
      alert("Maksimal 2 kartu Jam Istirahat per hari!");
      return;
    }
    if (schedules.some(s => s.day === day && s.period === period)) {
      alert(`Posisi istirahat tersebut pada hari ${day} sudah terisi!`);
      return;
    }

    const newBreakEntry: ScheduleEntry = {
      id: `break-${Date.now()}-${period}`,
      day,
      period,
      time: '',
      subject: 'Jam Istirahat',
      class: 'Istirahat',
      isBreak: true
    };

    setSchedules([...schedules, newBreakEntry]);
    setIsBreakModalOpen(false);
  };

  const triggerDelete = (id: string) => {
    setConfirmDelete({ isOpen: true, id });
  };

  const executeDelete = () => {
    setSchedules(schedules.filter(s => s.id !== confirmDelete.id));
    setConfirmDelete({ isOpen: false, id: '' });
  };

  const handleOpenModal = () => {
    if (availableClasses.length === 0) {
        alert("Anda belum memiliki plotting kelas dari Admin. Silahkan hubungi Admin untuk menentukan beban mengajar Anda.");
        return;
    }
    setIsModalOpen(true);
  };

  const periodColors = [
    { 
      bg: 'bg-gradient-to-br from-blue-600 to-indigo-700 dark:from-blue-700 dark:to-indigo-900', 
      border: 'border-blue-700/50 dark:border-indigo-950/60', 
      text: 'text-white', 
      accent: 'bg-white/20 text-white border border-white/10', 
      icon: 'text-blue-100' 
    },
    { 
      bg: 'bg-gradient-to-br from-rose-500 to-pink-600 dark:from-rose-600 dark:to-pink-800', 
      border: 'border-rose-600/50 dark:border-pink-950/60', 
      text: 'text-white', 
      accent: 'bg-white/20 text-white border border-white/10', 
      icon: 'text-rose-100' 
    },
    { 
      bg: 'bg-gradient-to-br from-amber-500 to-orange-600 dark:from-amber-600 dark:to-orange-800', 
      border: 'border-amber-600/50 dark:border-orange-950/60', 
      text: 'text-white', 
      accent: 'bg-white/20 text-white border border-white/10', 
      icon: 'text-amber-100' 
    },
    { 
      bg: 'bg-gradient-to-br from-emerald-500 to-teal-650 dark:from-emerald-650 dark:to-teal-850', 
      border: 'border-emerald-600/50 dark:border-teal-950/60', 
      text: 'text-white', 
      accent: 'bg-white/20 text-white border border-white/10', 
      icon: 'text-emerald-100' 
    },
    { 
      bg: 'bg-gradient-to-br from-fuchsia-500 to-purple-600 dark:from-fuchsia-600 dark:to-purple-800', 
      border: 'border-fuchsia-600/50 dark:border-purple-950/60', 
      text: 'text-white', 
      accent: 'bg-white/20 text-white border border-white/10', 
      icon: 'text-fuchsia-100' 
    },
    { 
      bg: 'bg-gradient-to-br from-violet-600 to-purple-700 dark:from-violet-700 dark:to-purple-900', 
      border: 'border-violet-700/50 dark:border-purple-950/60', 
      text: 'text-white', 
      accent: 'bg-white/20 text-white border border-white/10', 
      icon: 'text-violet-100' 
    },
    { 
      bg: 'bg-gradient-to-br from-cyan-500 to-blue-600 dark:from-cyan-600 dark:to-blue-800', 
      border: 'border-cyan-600/50 dark:border-blue-950/60', 
      text: 'text-white', 
      accent: 'bg-white/20 text-white border border-white/10', 
      icon: 'text-cyan-100' 
    },
    { 
      bg: 'bg-gradient-to-br from-orange-500 to-red-600 dark:from-orange-600 dark:to-red-800', 
      border: 'border-orange-600/50 dark:border-red-950/60', 
      text: 'text-white', 
      accent: 'bg-white/20 text-white border border-white/10', 
      icon: 'text-orange-100' 
    },
  ];

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;

    if (over && over.id.toString().startsWith('slot-')) {
      const entryId = active.id.toString();
      const targetData = over.data.current as { day: string; period: number };
      
      const entryToMove = schedules.find(s => s.id === entryId);
      if (!entryToMove) return;

      // Check if target slot is already occupied
      const isOccupied = schedules.some(s => s.day === targetData.day && s.period === targetData.period && s.id !== entryId);
      
      if (isOccupied) {
        // Swap or just alert? Let's try to swap if it's the same subject/class or just prevent if different
        // For simplicity, let's just prevent move if occupied by another entry
        return;
      }

      const updatedSchedules = schedules.map(s => {
        if (s.id === entryId) {
          return { ...s, day: targetData.day as any, period: targetData.period };
        }
        return s;
      });

      setSchedules(updatedSchedules);
    }
  };

  const activeEntry = activeId ? schedules.find(s => s.id === activeId) : null;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Hanya file PDF yang diperbolehkan.');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', 'Dokumen');

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Gagal mengupload file');

      const result = await response.json();
      if (result.url) {
        await onUpdateSettings({
          ...schoolSettings,
          academicCalendarUrl: result.url
        });
        alert('Kalender pendidikan berhasil diperbarui.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Terjadi kesalahan saat mengupload file.');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="space-y-6 animate-fade-in pb-20">
        {/* Tab Navigation */}
        <div className="flex bg-white dark:bg-slate-900 p-1.5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 w-fit mx-auto mb-6">
          {userRole !== UserRole.Admin ? (
            <>
              <button
                onClick={() => setActiveTab('jadwal')}
                className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all ${
                  activeTab === 'jadwal'
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                }`}
              >
                Jadwal Mengajar
              </button>
              <button
                onClick={() => setActiveTab('kalender')}
                className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all ${
                  activeTab === 'kalender'
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                }`}
              >
                Kalender Pembelajaran
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setActiveTab('kalender')}
                className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all ${
                  activeTab === 'kalender'
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                }`}
              >
                Kalender Pendidikan (PDF)
              </button>
              <button
                onClick={() => setActiveTab('periode')}
                className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all ${
                  activeTab === 'periode'
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                }`}
              >
                Tahun Pelajaran & Transisi ⚡
              </button>
            </>
          )}
        </div>

        {activeTab === 'jadwal' && userRole !== UserRole.Admin ? (
          <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-3xl shadow-xs border border-slate-200 dark:border-slate-800">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-5 mb-8 border-b border-slate-100 dark:border-slate-800/80 pb-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-800 dark:text-white flex items-center gap-3 tracking-tight">
                  <Clock className="text-indigo-600 dark:text-indigo-400" size={28} />
                  <span>Jadwal Mengajar</span>
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <span>Plotting urutan jam mengajar</span>
                  <span className="px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-black border border-indigo-100 dark:border-indigo-900/40">
                    {teacherSubject}
                  </span>
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-2.5 w-full lg:w-auto">
                <button
                  onClick={handlePrintSchedule}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold text-xs sm:text-sm rounded-xl border border-slate-200 dark:border-slate-700 transition-all cursor-pointer shadow-2xs"
                  title="Cetak Jadwal Mengajar"
                >
                  <Printer size={16} />
                  <span>Cetak</span>
                </button>

                <button
                  onClick={handleExportExcel}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 font-bold text-xs sm:text-sm rounded-xl border border-emerald-200/80 dark:border-emerald-800/60 transition-all cursor-pointer shadow-2xs"
                  title="Ekspor ke Excel"
                >
                  <FileSpreadsheet size={16} className="text-emerald-600 dark:text-emerald-400" />
                  <span>Excel</span>
                </button>

                <button
                  onClick={() => setIsBreakModalOpen(true)}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs sm:text-sm rounded-xl shadow-xs shadow-amber-500/20 transition-all cursor-pointer"
                  title="Tambah Jam Istirahat (Maks 2 per hari)"
                >
                  <Coffee size={16} />
                  <span>Tambah Jam Istirahat</span>
                </button>

                <button 
                  onClick={handleOpenModal} 
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-xs shadow-indigo-500/20 transition-all cursor-pointer"
                >
                  <Plus size={16} />
                  <span>Tambah Jam Mengajar</span>
                </button>
              </div>
            </div>
        
        {availableClasses.length === 0 && (
            <div className="mb-8 p-6 bg-amber-50 dark:bg-amber-500/10 border-2 border-dashed border-amber-200 dark:border-amber-500/30 rounded-[2rem] flex items-center gap-5">
                <div className="p-3 bg-amber-500 text-white rounded-2xl shadow-lg">
                    <ShieldAlert size={24} />
                </div>
                <div>
                    <p className="font-black text-amber-800 dark:text-amber-400 text-sm uppercase tracking-widest">Belum Ada Plotting Kelas</p>
                    <p className="text-xs text-amber-600 dark:text-amber-500/80 font-medium">Anda belum bisa menambah jadwal karena Admin belum menetapkan kelas mengajar untuk akun Anda.</p>
                </div>
            </div>
        )}

        {/* Mobile View */}
        <div className="xl:hidden grid grid-cols-2 gap-3">
          {days.map(day => {
            const daySchedules = schedules.filter(s => s.day === day).sort((a, b) => a.period - b.period);
            return (
              <div key={day} className="bg-slate-50/50 dark:bg-slate-800/30 border border-slate-200/50 dark:border-slate-800/50 p-3.5 rounded-2xl flex flex-col text-left">
                <h3 className="text-xs font-black text-slate-700 dark:text-slate-350 uppercase tracking-widest mb-3 text-center bg-slate-100 dark:bg-slate-800 px-2 py-1.5 rounded-xl border border-slate-200/45 dark:border-slate-700/40">{day}</h3>
                <div className="space-y-2 flex-1 flex flex-col justify-start">
                  {daySchedules.length > 0 ? daySchedules.map((s, idx) => {
                    const isBreak = s.isBreak || s.subject === 'Jam Istirahat';
                    if (isBreak) {
                      const baseP = Math.floor(s.period);
                      const prevPt = getPeriodTime(baseP, day);
                      const nextPt = getPeriodTime(baseP + 1, day);
                      const timeStr = prevPt?.endTime && nextPt?.startTime ? `${prevPt.endTime} - ${nextPt.startTime}` : `Sesudah Jam ${baseP}`;
                      return (
                        <div key={s.id} className="p-2.5 bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/40 rounded-xl shadow-2xs flex items-center justify-between text-emerald-900 dark:text-emerald-200">
                          <div className="flex items-center gap-2">
                            <Coffee size={14} className="text-emerald-500" />
                            <div className="text-left">
                              <span className="text-[10px] font-black uppercase tracking-wider block text-emerald-800 dark:text-emerald-300">☕ Istirahat</span>
                              <span className="text-[8px] font-bold text-emerald-700 dark:text-emerald-400">Pukul {timeStr}</span>
                            </div>
                          </div>
                          <button onClick={() => triggerDelete(s.id)} className="p-1 text-emerald-600 hover:text-rose-500 dark:text-emerald-400 dark:hover:text-rose-400"><Trash2 size={11} /></button>
                        </div>
                      );
                    }
                    const colorTheme = periodColors[s.period % periodColors.length];
                    const pt = getPeriodTime(s.period, day);
                    return (
                    <div key={s.id} className={`p-2.5 ${colorTheme.bg} border ${colorTheme.border} ${colorTheme.text || 'text-white'} rounded-xl shadow-md flex flex-col text-left relative group`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-wider ${colorTheme.accent}`}>
                            Jam {s.period}
                          </span>
                          <button 
                            onClick={() => triggerDelete(s.id)}
                            className="text-white/70 hover:text-white p-0.5 rounded"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                        {pt && <span className="text-[9px] font-bold text-white/80 flex items-center gap-0.5"><Clock size={9} className="text-white/70" /> {pt.startTime}</span>}
                        <span className="text-[11px] font-black text-white mt-1 truncate">Kls {s.class}</span>
                    </div>
                  )}) : (
                    <div className="p-4 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl text-center text-slate-400 dark:text-slate-500 text-[10px] font-bold italic my-auto">Kosong</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop View - Adjusted to 5 columns for Mon-Fri */}
        <div className="hidden xl:grid grid-cols-5 gap-6">
          {days.map(day => (
            <div key={day} className="space-y-5">
              <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-800/40 py-3 rounded-2xl text-center font-black text-slate-700 dark:text-slate-200 text-xs uppercase tracking-[0.2em] shadow-2xs">{day}</div>
              <div className="space-y-3">
                {periods.map(p => {
                  const entry = schedules.find(s => s.day === day && s.period === p && !s.isBreak && s.subject !== 'Jam Istirahat');
                  const breakEntry = schedules.find(s => s.day === day && (s.isBreak || s.subject === 'Jam Istirahat') && s.period === p + 0.5);
                  const colorTheme = periodColors[p % periodColors.length];
                  return (
                    <React.Fragment key={p}>
                      <DroppableSlot day={day} period={p}>
                        {entry ? (
                          <DraggableEntry entry={entry} colorTheme={colorTheme} onDelete={triggerDelete} periodTime={getPeriodTime(p, day)} />
                        ) : (
                          <div className="h-[96px] border-2 border-dashed border-slate-200/50 dark:border-slate-800/70 rounded-2xl flex flex-col items-center justify-center opacity-40 hover:opacity-100 hover:bg-slate-50/50 dark:hover:bg-slate-850/20 hover:border-slate-300 dark:hover:border-slate-700 transition-all cursor-pointer">
                             <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">JAM {p}</span>
                             {getPeriodTime(p, day) && (
                               <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500/80 mt-1 flex items-center gap-1">
                                 <Clock size={10} /> {getPeriodTime(p, day)?.startTime}
                               </span>
                             )}
                          </div>
                        )}
                      </DroppableSlot>
                      {breakEntry && (() => {
                        const prevPt = getPeriodTime(p, day);
                        const nextPt = getPeriodTime(p + 1, day);
                        const timeStr = prevPt?.endTime && nextPt?.startTime ? `${prevPt.endTime} - ${nextPt.startTime}` : `Sesudah Jam ${p}`;
                        return (
                          <div key={breakEntry.id} className="p-3 bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/40 rounded-2xl shadow-2xs flex items-center justify-between text-emerald-900 dark:text-emerald-200 my-2 text-left">
                             <div className="flex items-center gap-2">
                               <Coffee size={16} className="text-emerald-500" />
                               <div className="text-left">
                                 <p className="text-xs font-black uppercase tracking-wider text-emerald-800 dark:text-emerald-300">☕ Jam Istirahat</p>
                                 <p className="text-[9px] font-bold text-emerald-700 dark:text-emerald-400 flex items-center gap-0.5 mt-0.5">
                                   <Clock size={9} /> {timeStr}
                                 </p>
                               </div>
                             </div>
                             <button 
                               onClick={() => triggerDelete(breakEntry.id)}
                               className="p-1.5 text-emerald-600 hover:text-rose-600 dark:text-emerald-400 dark:hover:text-rose-400 bg-emerald-100/50 dark:bg-emerald-900/30 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl transition-all"
                               title="Hapus Istirahat"
                             >
                               <Trash2 size={12} />
                             </button>
                          </div>
                        );
                      })()}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <DragOverlay dropAnimation={{
          sideEffects: defaultDropAnimationSideEffects({
            styles: {
              active: {
                opacity: '0.5',
              },
            },
          }),
        }}>
          {activeEntry ? (() => {
            const colorTheme = periodColors[activeEntry.period % periodColors.length];
            const pt = getPeriodTime(activeEntry.period, activeEntry.day);
            return (
              <div className={`p-4 ${colorTheme.bg} border ${colorTheme.border} ${colorTheme.text || 'text-white'} rounded-2xl shadow-2xl scale-105 rotate-2 opacity-95 text-left`}>
                 <div className="flex justify-between items-start mb-2">
                   <div className="flex flex-col gap-1">
                     <span className={`text-[10px] font-black px-2 py-0.5 rounded-lg uppercase tracking-wider w-fit ${colorTheme.accent}`}>
                       JAM {activeEntry.period}
                     </span>
                     {pt && (
                       <span className="text-[11px] font-bold text-white/80 flex items-center gap-1 mt-1">
                         <Clock size={12} /> {pt.startTime} - {pt.endTime}
                       </span>
                     )}
                   </div>
                 </div>
                 <p className="text-xs font-black text-white leading-tight mb-2 truncate">{activeEntry.subject}</p>
                 <div className="flex items-center gap-1 text-[11px] font-black text-white bg-white/15 border border-white/10 px-2 py-0.5 rounded-lg w-fit">
                   <MapPin size={10} className="text-white" />
                   <span>Kelas {activeEntry.class}</span>
                 </div>
              </div>
            );
          })() : null}
        </DragOverlay>

        <div className="mt-12 p-8 bg-slate-50 dark:bg-slate-800/30 rounded-3xl border border-slate-100 dark:border-slate-800 flex items-start gap-5">
            <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg">
                <Info size={24} />
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
              <p className="font-black text-slate-800 dark:text-white mb-2 uppercase tracking-widest text-sm">Informasi Jam Pelajaran:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Daftar kelas di dropdown "Pilih Rombel" dibatasi hanya pada kelas yang sudah di-plotting Admin untuk Anda.</li>
                <li>Halaman ini menampilkan jadwal aktif dari Senin sampai Jumat. Hari Sabtu ditiadakan sesuai regulasi.</li>
                <li>Input rentang jam membantu Anda mengisi jadwal blok (misal: Jam 1 s/d 3).</li>
              </ul>
            </div>
        </div>
      </div>
        ) : activeTab === 'periode' && userRole === UserRole.Admin ? (
          <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800 text-left space-y-8 animate-fade-in">
            <div>
              <h2 className="text-3xl font-black text-slate-800 dark:text-white flex items-center gap-3 tracking-tight leading-none">
                <RefreshCw className="text-indigo-650" size={32} />
                Tahun Pelajaran & Semester Aktif
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-semibold mt-3">
                Ubah informasi Tahun Pelajaran dan Semester yang sedang berjalan secara sistemik.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-100 dark:border-slate-800">
              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Tahun Pelajaran</label>
                <input 
                  type="text" 
                  value={academicYear}
                  onChange={e => setAcademicYear(e.target.value)}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                  placeholder="Contoh: 2024/2025"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 ml-1">Semester</label>
                <select 
                  value={semester}
                  onChange={e => setSemester(e.target.value)}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 dark:text-white font-bold transition-all"
                >
                  <option value="">Pilih Semester</option>
                  <option value="1 (Ganjil)">1 (Ganjil)</option>
                  <option value="2 (Genap)">2 (Genap)</option>
                </select>
              </div>

              <div className="md:col-span-2 flex justify-end">
                <button
                  type="button"
                  onClick={handleSavePeriodOnly}
                  disabled={isSavingPeriod}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-wider rounded-2xl flex items-center gap-2 transition-all active:scale-95 shadow-lg shadow-indigo-500/20"
                >
                  {isSavingPeriod ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : isSavedPeriod ? (
                    <CheckCircle size={14} />
                  ) : (
                    <Save size={14} />
                  )}
                  {isSavingPeriod ? 'Menyimpan...' : isSavedPeriod ? 'Tersimpan!' : 'Simpan Periode Akademik'}
                </button>
              </div>
            </div>

            {/* Transition Helper Section Card */}
            <div className="p-8 bg-amber-50 dark:bg-slate-800/40 border border-amber-200/50 dark:border-slate-800/80 rounded-[2rem] flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
              <div className="space-y-1.5">
                <h4 className="font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                  <span className="p-1 px-2 text-[9px] bg-amber-500 text-white rounded-lg uppercase tracking-wider font-black animate-pulse">WIZARD</span>
                  Asisten Transisi Periode Akademik ⚡
                </h4>
                <p className="text-xs text-slate-650 dark:text-slate-400 font-semibold leading-relaxed">
                  Gunakan asisten ini untuk melakukan pembersihan data kehadiran, agenda, nilai, dan melaksanakan kenaikan kelas otomatis dari kelas 7 ke 8, 8 ke 9, serta lulus alumni saat memasuki Tahun Pelajaran baru atau ganti semester.
                </p>
              </div>
              <button
                type="button"
                onClick={openTransitionWizard}
                className="px-6 py-3.5 bg-indigo-600 hover:bg-slate-900 dark:hover:bg-indigo-500 dark:bg-indigo-700 text-white text-xs font-black uppercase tracking-wider rounded-2xl flex items-center gap-2 transition-all shrink-0 active:scale-95 shadow-lg shadow-indigo-500/25"
              >
                <RefreshCw size={14} className="animate-spin" style={{ animationDuration: '4s' }} /> Mulai Transisi Baru
              </button>
            </div>

            {/* Graduated / Grade 9 Classes Transisi Card */}
            <div className="p-6 bg-gradient-to-br from-indigo-900 via-indigo-950 to-slate-900 text-white rounded-[2rem] shadow-xl border border-indigo-500/20 space-y-4 text-left">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className="p-3 bg-amber-500 text-white rounded-2xl shadow-lg shadow-amber-500/20 shrink-0">
                    <GraduationCap size={24} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-extrabold text-base text-white uppercase tracking-tight">
                        Kelas Lulus & Alumni Pada Transisi
                      </h4>
                      <span className="px-2.5 py-0.5 bg-amber-500/20 border border-amber-400/30 text-amber-300 text-[10px] font-black rounded-full uppercase tracking-wider">
                        Transisi Kelulusan
                      </span>
                    </div>
                    <p className="text-xs text-indigo-200 font-medium mt-1">
                      Siswa di kelas tingkat akhir (Kelas 9) berikut akan otomatis diluluskan dan diubah statusnya menjadi <strong className="text-amber-300">ALUMNI</strong> saat transisi.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-white/10 shrink-0 self-stretch sm:self-auto justify-between sm:justify-start">
                  <div>
                    <span className="text-[9px] text-indigo-200 uppercase font-bold block">Calon Alumni</span>
                    <span className="text-base font-black text-amber-300">{grade9StudentsCount} Siswa</span>
                  </div>
                  <div className="h-6 w-px bg-white/20 mx-1"></div>
                  <div>
                    <span className="text-[9px] text-indigo-200 uppercase font-bold block">Total Alumni</span>
                    <span className="text-base font-black text-white">{alumniStudentsCount} Siswa</span>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-indigo-200 font-bold">Daftar Rombel Kelas 9 (Yang Akan Lulus):</span>
                  {grade9ClassesList.length > 0 ? (
                    grade9ClassesList.map(cls => (
                      <span key={cls.name} className="px-3 py-1.5 bg-indigo-500/30 border border-indigo-400/40 rounded-xl text-xs font-black text-amber-300 flex items-center gap-1.5 shadow-sm">
                        <span>Kelas {cls.name}</span>
                        <span className="text-[10px] bg-indigo-500/50 text-white px-1.5 py-0.5 rounded-md font-bold">{cls.count} siswa</span>
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-slate-300 italic font-medium">Belum ada rombel kelas 9 terdeteksi</span>
                  )}
                </div>
              </div>

              {/* Detail Transisi Data Siswa & Agenda */}
              <div className="pt-4 border-t border-white/10 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  {/* View Tabs */}
                  <div className="flex items-center gap-2 bg-white/10 p-1 rounded-2xl border border-white/10 w-fit">
                    <button
                      type="button"
                      onClick={() => setViewTransitionDetail('siswa')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black transition-all ${
                        viewTransitionDetail === 'siswa'
                          ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                          : 'text-indigo-200 hover:text-white'
                      }`}
                    >
                      <Users size={14} />
                      Data Siswa ({transitionStudentsList.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewTransitionDetail('agenda')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black transition-all ${
                        viewTransitionDetail === 'agenda'
                          ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                          : 'text-indigo-200 hover:text-white'
                      }`}
                    >
                      <ClipboardList size={14} />
                      Agenda Pembelajaran ({transitionAgendasList.length})
                    </button>
                  </div>

                  {/* Search Input */}
                  <div className="relative flex-1 max-w-xs">
                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-indigo-300" />
                    <input
                      type="text"
                      value={transitionSearch}
                      onChange={(e) => setTransitionSearch(e.target.value)}
                      placeholder={viewTransitionDetail === 'siswa' ? "Cari nama, NISN, kelas..." : "Cari topik, mapel, guru..."}
                      className="w-full pl-9 pr-4 py-2 bg-white/10 border border-white/10 rounded-xl text-xs font-bold text-white placeholder:text-indigo-300/60 outline-none focus:ring-2 focus:ring-amber-400/50 transition-all"
                    />
                  </div>
                </div>

                {/* Filter Rombel / Class Pills */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none text-xs">
                  <span className="text-[10px] uppercase font-bold text-indigo-300 shrink-0 mr-1">Filter Kelas:</span>
                  <button
                    type="button"
                    onClick={() => setTransitionClassFilter('SEMUA')}
                    className={`px-3 py-1 rounded-lg font-black text-[11px] shrink-0 transition-all ${
                      transitionClassFilter === 'SEMUA'
                        ? 'bg-white text-indigo-950 shadow-md font-extrabold'
                        : 'bg-white/10 text-indigo-200 hover:bg-white/20'
                    }`}
                  >
                    Semua ({viewTransitionDetail === 'siswa' ? transitionStudentsList.length : transitionAgendasList.length})
                  </button>
                  {grade9ClassesList.map(cls => (
                    <button
                      key={cls.name}
                      type="button"
                      onClick={() => setTransitionClassFilter(cls.name)}
                      className={`px-3 py-1 rounded-lg font-black text-[11px] shrink-0 transition-all ${
                        transitionClassFilter === cls.name
                          ? 'bg-amber-400 text-slate-950 shadow-md font-extrabold'
                          : 'bg-white/10 text-indigo-200 hover:bg-white/20'
                      }`}
                    >
                      Kelas {cls.name}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setTransitionClassFilter('ALUMNI')}
                    className={`px-3 py-1 rounded-lg font-black text-[11px] shrink-0 transition-all ${
                      transitionClassFilter === 'ALUMNI'
                        ? 'bg-indigo-500 text-white shadow-md font-extrabold'
                        : 'bg-white/10 text-indigo-200 hover:bg-white/20'
                    }`}
                  >
                    Alumni (Lulus)
                  </button>
                </div>

                {/* Content Panel */}
                <div className="bg-slate-950/40 border border-white/10 rounded-2xl p-4 max-h-[450px] overflow-y-auto space-y-3 custom-scrollbar">
                  {viewTransitionDetail === 'siswa' ? (
                    filteredTransitionStudents.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filteredTransitionStudents.map((s) => {
                          const isAlumni = s.status === 'Tidak Aktif' || (s.class || '').toUpperCase() === 'ALUMNI';
                          return (
                            <div key={s.id} className="p-3.5 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm shrink-0 shadow-inner ${
                                isAlumni ? 'bg-indigo-600/60 text-indigo-200' : 'bg-amber-500/60 text-amber-100'
                              }`}>
                                {s.name ? s.name.charAt(0).toUpperCase() : 'S'}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h5 className="font-bold text-xs text-white truncate">{s.name}</h5>
                                <p className="text-[10px] text-indigo-200 font-medium">
                                  NISN: {s.nisn || s.nis || '-'} • JK: {s.gender || '-'}
                                </p>
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                  <span className="px-2 py-0.5 bg-white/10 rounded text-[9px] font-bold text-indigo-200">
                                    Kelas {s.class || '-'}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                                    isAlumni ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                  }`}>
                                    {isAlumni ? 'Alumni' : 'Calon Alumni'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-indigo-200/70 text-xs italic font-medium">
                        Siswa tidak ditemukan untuk filter ini.
                      </div>
                    )
                  ) : (
                    filteredTransitionAgendas.length > 0 ? (
                      <div className="space-y-3">
                        {filteredTransitionAgendas.map((a) => {
                          const isDone = a.status === 'Selesai';
                          return (
                            <div key={a.id} className="p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all space-y-2">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="px-2.5 py-0.5 bg-amber-400 text-slate-950 rounded-lg text-[10px] font-black uppercase">
                                    Kelas {a.class}
                                  </span>
                                  <span className="px-2.5 py-0.5 bg-indigo-500/40 text-indigo-200 rounded-lg text-[10px] font-bold">
                                    {a.subject}
                                  </span>
                                  {a.teacherName && (
                                    <span className="text-[11px] text-indigo-200 font-medium">
                                      • Guru: <strong className="text-white">{a.teacherName}</strong>
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                                    isDone ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                  }`}>
                                    {a.status || 'Selesai'}
                                  </span>
                                  <span className="text-[10px] font-bold text-indigo-300">
                                    {a.date ? new Date(a.date).toLocaleDateString('id-ID', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                  </span>
                                </div>
                              </div>

                              <div>
                                <h6 className="text-xs font-black text-amber-300 leading-snug">
                                  {a.topic || a.tpDescription || 'Pertemuan Pembelajaran'}
                                </h6>
                                {a.notes && (
                                  <p className="text-[11px] text-indigo-200/80 mt-1 line-clamp-2">
                                    {a.notes}
                                  </p>
                                )}
                              </div>

                              {(a.materialLink || a.documentationUrl) && (
                                <div className="flex items-center gap-2 pt-1 flex-wrap">
                                  {a.materialLink && (
                                    <a
                                      href={a.materialLink.startsWith('http') ? a.materialLink : `https://${a.materialLink}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-300 rounded-lg text-[10px] font-black transition-all"
                                    >
                                      <ExternalLink size={12} />
                                      Buka Materi Pembelajaran
                                    </a>
                                  )}
                                  {a.documentationUrl && (
                                    <a
                                      href={a.documentationUrl.startsWith('http') ? a.documentationUrl : `https://${a.documentationUrl}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-400/40 text-indigo-200 rounded-lg text-[10px] font-black transition-all"
                                    >
                                      <FileText size={12} />
                                      Dokumentasi
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-indigo-200/70 text-xs italic font-medium">
                        Agenda tidak ditemukan untuk filter ini.
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col h-[1000px]">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-800 dark:text-white flex items-center gap-3 tracking-tight leading-none">
                  <CalendarDays className="text-indigo-600" size={32} />
                  Kalender Pendidikan
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-3">
                  Informasi agenda akademik dan kalender pendidikan sekolah.
                </p>
              </div>
              
              {userRole === UserRole.Admin && (
                <div className="relative w-full md:w-auto flex flex-col sm:flex-row gap-3">
                  {schoolSettings.academicCalendarUrl && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (confirm('Apakah Anda yakin ingin menghapus Kalender Pendidikan saat ini?')) {
                          await onUpdateSettings({
                            ...schoolSettings,
                            academicCalendarUrl: ''
                          });
                          alert('Kalender Pendidikan berhasil dihapus.');
                        }
                      }}
                      className="flex items-center justify-center gap-2 px-6 py-3.5 bg-rose-600 text-white font-black rounded-2xl shadow-xl shadow-rose-500/20 hover:bg-rose-700 active:scale-95 transition-all w-full sm:w-auto"
                    >
                      <Trash2 size={20} />
                      Hapus
                    </button>
                  )}
                  <div className="relative w-full sm:w-auto">
                    <input
                      type="file"
                      id="calendar-upload"
                      className="hidden"
                      accept=".pdf"
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="calendar-upload"
                      className={`flex items-center justify-center gap-2 px-6 py-3.5 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 hover:bg-indigo-700 active:scale-95 transition-all cursor-pointer w-full sm:w-auto ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isUploading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />}
                      {schoolSettings.academicCalendarUrl ? 'Update Kalender (PDF)' : 'Upload Kalender (PDF)'}
                    </label>
                  </div>
                </div>
              )}
            </div>

            {schoolSettings.academicCalendarUrl ? (
              <div className="flex-1 flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-rose-100 dark:bg-rose-500/20 text-rose-600 rounded-lg">
                      <FileText size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-black text-slate-800 dark:text-white">Kalender Pendidikan Aktif</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Format: PDF</p>
                    </div>
                  </div>
                  <a 
                    href={schoolSettings.academicCalendarUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-5 py-2.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-black rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 transition-all shadow-sm w-full sm:w-auto"
                  >
                    <ExternalLink size={14} /> Buka di Tab Baru
                  </a>
                </div>
                
                <div className="relative flex-1 bg-slate-100 dark:bg-slate-800 rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-inner">
                  <iframe 
                    src={`${schoolSettings.academicCalendarUrl}#toolbar=0&navpanes=0&scrollbar=1`} 
                    className="absolute inset-0 w-full h-full border-none"
                    title="Kalender Pendidikan"
                  />
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-20 bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
                <div className="w-20 h-20 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 rounded-3xl flex items-center justify-center mb-6">
                  <CalendarDays size={40} />
                </div>
                <h3 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight mb-2">Belum Ada Kalender</h3>
                <p className="text-slate-500 dark:text-slate-400 font-medium max-w-md px-6">
                  {userRole === UserRole.Admin 
                    ? 'Silahkan upload file PDF kalender pendidikan untuk ditampilkan di sini agar dapat diakses oleh seluruh warga sekolah.' 
                    : 'Kalender pendidikan belum diunggah oleh Admin. Silahkan hubungi bagian kurikulum.'}
                </p>
              </div>
            )}
          </div>
        )}

      {/* MODAL TAMBAH JADWAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-md animate-fade-in-up border border-white/10 overflow-hidden">
                <div className="p-8 pb-0 flex justify-between items-center">
                    <h3 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">Tambah Jam Mengajar</h3>
                    <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"><X className="text-slate-400" /></button>
                </div>
                
                <div className="p-8 pt-6 space-y-6">
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Hari Mengajar</label>
                      <select 
                        value={newEntry.day} 
                        onChange={e => setNewEntry({...newEntry, day: e.target.value as any})} 
                        className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold dark:text-white transition-all focus:ring-4 focus:ring-indigo-500/10 appearance-none cursor-pointer"
                      >
                        {days.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Mulai Jam Ke-</label>
                          <select 
                            value={newEntry.startPeriod} 
                            onChange={e => handleStartPeriodChange(Number(e.target.value))} 
                            className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold dark:text-white transition-all focus:ring-4 focus:ring-indigo-500/10 appearance-none cursor-pointer"
                          >
                            {periods.map(p => {
                                const pt = getPeriodTime(p, newEntry.day);
                                return (
                                <option key={p} value={p}>
                                    Jam Ke-{p} {pt ? `(${pt.startTime})` : ''}
                                </option>
                                );
                            })}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Sampai Jam Ke-</label>
                          <select 
                            value={newEntry.endPeriod} 
                            onChange={e => handleEndPeriodChange(Number(e.target.value))} 
                            className={`w-full px-5 py-4 border rounded-2xl outline-none font-bold transition-all focus:ring-4 appearance-none cursor-pointer ${
                                newEntry.endPeriod < newEntry.startPeriod 
                                ? 'bg-rose-50 border-rose-200 text-rose-600 focus:ring-rose-500/10' 
                                : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 dark:text-white focus:ring-indigo-500/10'
                            }`}
                          >
                            {periods.map(p => {
                                const pt = getPeriodTime(p, newEntry.day);
                                return (
                                <option key={p} value={p} disabled={p < newEntry.startPeriod}>
                                    Jam Ke-{p} {pt ? `(${pt.startTime})` : ''} {p < newEntry.startPeriod ? '(Tidak Valid)' : ''}
                                </option>
                                );
                            })}
                          </select>
                        </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Mata Pelajaran</label>
                      <div className="w-full px-5 py-4 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-2xl font-black text-indigo-600">
                        {teacherSubject}
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Pilih Rombel (Kelas Ter-plotting)</label>
                      <select 
                        value={newEntry.class} 
                        onChange={e => setNewEntry({...newEntry, class: e.target.value})} 
                        className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold dark:text-white transition-all focus:ring-4 focus:ring-indigo-500/10 appearance-none cursor-pointer"
                      >
                        {availableClasses.map(c => <option key={c} value={c}>Kelas {c}</option>)}
                      </select>
                    </div>

                    <button 
                      onClick={handleAdd} 
                      className="w-full mt-4 py-5 bg-indigo-600 text-white font-black rounded-3xl shadow-xl shadow-indigo-500/20 hover:bg-indigo-700 active:scale-95 transition-all uppercase tracking-widest text-sm"
                    >
                      PLOT KE JADWAL
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* MODAL TAMBAH JAM ISTIRAHAT */}
      {isBreakModalOpen && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-md animate-fade-in-up border border-white/10 overflow-hidden">
                <div className="p-8 pb-0 flex justify-between items-center">
                    <h3 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight flex items-center gap-2">
                      <Coffee className="text-amber-500" size={24} />
                      Tambah Jam Istirahat
                    </h3>
                    <button onClick={() => setIsBreakModalOpen(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"><X className="text-slate-400" /></button>
                </div>
                
                <div className="p-8 pt-6 space-y-6">
                    <div className="p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-2xl">
                      <p className="text-xs text-amber-800 dark:text-amber-300 font-bold">
                        💡 Catatan: Maksimal 2 kartu Jam Istirahat per hari.
                      </p>
                    </div>

                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Hari</label>
                      <select 
                        value={breakEntry.day} 
                        onChange={e => setBreakEntry({...breakEntry, day: e.target.value as any})} 
                        className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold dark:text-white transition-all focus:ring-4 focus:ring-amber-500/10 appearance-none cursor-pointer"
                      >
                        {days.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Posisi Jam Istirahat</label>
                      <select 
                        value={breakEntry.period} 
                        onChange={e => setBreakEntry({...breakEntry, period: Number(e.target.value)})} 
                        className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold dark:text-white transition-all focus:ring-4 focus:ring-amber-500/10 appearance-none cursor-pointer"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(p => {
                            const breakPeriod = p + 0.5;
                            return (
                            <option key={breakPeriod} value={breakPeriod}>
                                Sesudah Jam Ke-{p} (Antara Jam {p} & {p + 1}) {p === 4 ? '⭐ (Istirahat Utama)' : ''}
                            </option>
                            );
                        })}
                      </select>
                    </div>

                    <button 
                      onClick={handleAddBreak} 
                      className="w-full mt-4 py-5 bg-amber-500 text-white font-black rounded-3xl shadow-xl shadow-amber-500/20 hover:bg-amber-600 active:scale-95 transition-all uppercase tracking-widest text-sm"
                    >
                      TAMBAHKAN JAM ISTIRAHAT
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Modal Delete Confirmation */}
      {confirmDelete.isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md animate-fade-in" onClick={() => setConfirmDelete({isOpen: false, id: ''})}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-rose-500/20 text-center">
            <div className="p-10">
              <div className="w-20 h-20 bg-rose-50 dark:bg-rose-500/10 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={40} />
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Hapus Slot?</h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-8 leading-relaxed">
                Slot jam pelajaran ini akan dikosongkan kembali.
              </p>
              <div className="flex flex-col gap-3">
                <button 
                  type="button" 
                  onClick={executeDelete} 
                  className="w-full py-4 bg-rose-500 text-white font-black rounded-2xl hover:bg-rose-600 active:scale-95 transition-all shadow-xl shadow-rose-500/20 uppercase tracking-widest text-xs"
                >
                  Ya, Hapus Sekarang
                </button>
                <button 
                  type="button" 
                  onClick={() => setConfirmDelete({isOpen: false, id: ''})} 
                  className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all uppercase tracking-widest text-xs"
                >
                  Batalkan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TRANSITION WIZARD MODAL */}
      {showTransitionModal && (
        <div className="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-md z-[130] flex items-center justify-center p-4 overflow-y-auto animate-fade-in text-slate-800 dark:text-slate-100">
          <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-slate-800 p-8 sm:p-10 space-y-6 max-h-[90vh] overflow-y-auto relative animate-scale-up text-left">
            
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-500 text-white rounded-2xl shadow-lg shadow-amber-500/20">
                  <RefreshCw className="animate-spin text-white" style={{ animationDuration: '6s' }} size={24} />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Asisten Transisi Akademik</h3>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">TAHUN AJARAN BARU & SEMESTER BARU</p>
                </div>
              </div>
              {!isTransitioning && (
                <button 
                  onClick={() => setShowTransitionModal(false)}
                  className="p-2 text-slate-400 hover:text-rose-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {/* ERROR DISPLAY */}
            {transitionError && (
              <div className="p-5 bg-rose-500/10 border border-rose-500/20 rounded-2xl text-rose-600 dark:text-rose-400 text-xs font-bold flex items-center gap-3">
                <AlertTriangle size={18} />
                <span>{transitionError}</span>
              </div>
            )}

            {/* STEP 1: CONFIGURATION */}
            {transitionStep === 1 && (
              <div className="space-y-6 text-left">
                <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">
                  Asisten ini akan mengonfigurasi website sekolah menuju periode akademik berikutnya secara otomatis dan aman. Silakan periksa detail di bawah ini.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 text-left">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Target Tahun Pelajaran</label>
                    <input 
                      type="text" 
                      value={targetYear}
                      onChange={e => setTargetYear(e.target.value)}
                      className="w-full bg-transparent outline-none dark:text-white font-black text-base border-b border-indigo-500/20 pb-1"
                      placeholder="Contoh: 2026/2027"
                    />
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 text-left">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Target Semester</label>
                    <select 
                      value={targetSemester}
                      onChange={e => setTargetSemester(e.target.value)}
                      className="w-full bg-transparent outline-none dark:text-white font-black text-base cursor-pointer border-b border-indigo-500/20 pb-1"
                    >
                      <option value="1 (Ganjil)">1 (Ganjil)</option>
                      <option value="2 (Genap)">2 (Genap)</option>
                    </select>
                  </div>
                </div>

                {/* Automation Toggles */}
                <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 text-left">Pilihan Aksi Transisi</h4>
                  
                  {/* Class Promotion */}
                  <div 
                    onClick={() => setShouldPromoteStudents(!shouldPromoteStudents)}
                    className={`p-4 rounded-2xl border cursor-pointer transition-all flex items-center justify-between ${
                      shouldPromoteStudents 
                        ? 'bg-indigo-50/40 border-indigo-500/20 dark:bg-indigo-500/5 dark:border-indigo-500/20' 
                        : 'bg-slate-50/40 border-slate-200 dark:bg-slate-800/20 dark:border-slate-705/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input 
                        type="checkbox" 
                        checked={shouldPromoteStudents} 
                        onChange={() => {}}
                        className="mt-1"
                      />
                      <div className="text-left">
                        <h5 className="text-xs font-black text-slate-800 dark:text-white leading-none">Proses Kenaikan Kelas Siswa</h5>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1 text-left">Naikkan otomatis: Kelas 7 → 8, Kelas 8 → 9, dan Kelas 9 → ALUMNI.</p>
                        
                        {grade9ClassesList.length > 0 && (
                          <div className="mt-2.5 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center gap-2 flex-wrap text-left">
                            <span className="text-[10px] font-black text-amber-700 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1">
                              <GraduationCap size={13} /> Rombel Kelas 9 Lulus:
                            </span>
                            {grade9ClassesList.map(cls => (
                              <span key={cls.name} className="px-2 py-0.5 bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-600/40 rounded-lg text-[10px] font-black text-amber-800 dark:text-amber-300">
                                {cls.name} ({cls.count} siswa)
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Clear Attendance */}
                  <div 
                    onClick={() => setClearAttendance(!clearAttendance)}
                    className={`p-4 rounded-2xl border cursor-pointer transition-all flex items-center justify-between ${
                      clearAttendance 
                        ? 'bg-rose-50/40 border-rose-500/20 dark:bg-rose-500/5 dark:border-rose-500/20' 
                        : 'bg-slate-50/40 border-slate-200 dark:bg-slate-800/20 dark:border-slate-705/30'
                    }`}
                  >
                    <div className="flex items-start gap-3 text-left">
                      <input 
                        type="checkbox" 
                        checked={clearAttendance} 
                        onChange={() => {}}
                        className="mt-1"
                      />
                      <div className="text-left">
                        <h5 className="text-xs font-black text-slate-800 dark:text-white leading-none">Kosongkan Laporan Kehadiran</h5>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1 text-left">Hapus semua data presensi harian untuk memulai absensi periode baru.</p>
                      </div>
                    </div>
                  </div>

                  {/* Clear Grades */}
                  <div 
                    onClick={() => setClearGrades(!clearGrades)}
                    className={`p-4 rounded-2xl border cursor-pointer transition-all flex items-center justify-between ${
                      clearGrades 
                        ? 'bg-rose-50/40 border-rose-500/20 dark:bg-rose-500/5 dark:border-rose-500/20' 
                        : 'bg-slate-50/40 border-slate-200 dark:bg-slate-800/20 dark:border-slate-705/30'
                    }`}
                  >
                    <div className="flex items-start gap-3 text-left">
                      <input 
                        type="checkbox" 
                        checked={clearGrades} 
                        onChange={() => {}}
                        className="mt-1"
                      />
                      <div className="text-left">
                        <h5 className="text-xs font-black text-slate-800 dark:text-white leading-none">Kosongkan/Reset Nilai Pembelajaran</h5>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1 text-left">Bersihkan semua nilai formatif TP, sumatif, dan nilai rapor.</p>
                      </div>
                    </div>
                  </div>

                  {/* Clear Agenda */}
                  <div 
                    onClick={() => setClearAgendas(!clearAgendas)}
                    className={`p-4 rounded-2xl border cursor-pointer transition-all flex items-center justify-between ${
                      clearAgendas 
                        ? 'bg-indigo-50/40 border-indigo-500/20 dark:bg-indigo-500/5 dark:border-indigo-500/20' 
                        : 'bg-slate-50/40 border-slate-200 dark:bg-slate-800/20 dark:border-slate-705/30'
                    }`}
                  >
                    <div className="flex items-start gap-3 text-left">
                      <input 
                        type="checkbox" 
                        checked={clearAgendas} 
                        onChange={() => {}}
                        className="mt-1 font-black text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="text-left">
                        <h5 className="text-xs font-black text-slate-800 dark:text-white leading-none">Arsip & Sembunyikan Agenda Kegiatan Mengajar</h5>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1 text-left">Arsipkan seluruh agenda harian dari para guru ke riwayat arsip agar tidak muncul di rekap aktif periode baru.</p>
                      </div>
                    </div>
                  </div>

                  {/* Clear Assignments */}
                  <div 
                    onClick={() => setClearAssignments(!clearAssignments)}
                    className={`p-4 rounded-2xl border cursor-pointer transition-all flex items-center justify-between ${
                      clearAssignments 
                        ? 'bg-rose-50/40 border-rose-500/20 dark:bg-rose-500/5 dark:border-rose-500/20' 
                        : 'bg-slate-50/40 border-slate-200 dark:bg-slate-800/20 dark:border-slate-705/30'
                    }`}
                  >
                    <div className="flex items-start gap-3 text-left">
                      <input 
                        type="checkbox" 
                        checked={clearAssignments} 
                        onChange={() => {}}
                        className="mt-1"
                      />
                      <div className="text-left">
                        <h5 className="text-xs font-black text-slate-800 dark:text-white leading-none">Kosongkan Bank Tugas & Pengumpulan</h5>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1 text-left">Hapus semua data tugas, file penugasan, dan pengumpulan siswa.</p>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
                  <button 
                    type="button"
                    onClick={() => setShowTransitionModal(false)}
                    className="px-5 py-2.5 bg-slate-100 text-slate-650 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 text-xs font-black uppercase tracking-wider rounded-xl transition-all"
                  >
                    Batal
                  </button>
                  <button 
                    type="button"
                    onClick={() => setTransitionStep(2)}
                    className="px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-lg flex items-center gap-1.5"
                  >
                    Berikutnya <RefreshCw size={12} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: CONFIRMATION */}
            {transitionStep === 2 && !isTransitioning && (
              <div className="space-y-6 text-left animate-fade-in">
                <div className="p-6 bg-rose-500/10 border border-rose-500/25 rounded-3xl flex items-start gap-4 text-left">
                  <div className="p-2.5 bg-rose-500 text-white rounded-2xl shrink-0 shadow-lg shadow-rose-500/10">
                    <AlertTriangle size={20} />
                  </div>
                  <div className="space-y-1 text-left">
                    <h4 className="font-extrabold text-rose-600 dark:text-rose-400 uppercase tracking-widest text-[10px]">Tindakan Kritis & Permanen</h4>
                    <p className="text-xs text-slate-660 dark:text-slate-300 font-semibold leading-relaxed">
                      Proses ini permanen. Seluruh data lama yang dipilih akan dihapus secara fisik dan permanen dari database Cloud Firestore.
                    </p>
                  </div>
                </div>

                <div className="space-y-2 bg-slate-50 dark:bg-slate-800/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 text-xs text-left">
                  <h4 className="font-black text-slate-400 uppercase tracking-widest mb-1.5 text-[10px]">Rencana Eksekusi:</h4>
                  <div className="flex justify-between font-bold py-1 border-b border-dashed border-slate-200 dark:border-slate-800">
                    <span className="text-slate-500">Tahun Pelajaran Baru</span>
                    <span className="text-indigo-600 font-black">{targetYear}</span>
                  </div>
                  <div className="flex justify-between font-bold py-1 border-b border-dashed border-slate-200 dark:border-slate-800">
                    <span className="text-slate-500">Semester Baru</span>
                    <span className="text-indigo-600 font-black">{targetSemester}</span>
                  </div>
                  <div className="flex justify-between font-bold py-1 border-b border-dashed border-slate-200 dark:border-slate-800">
                    <span className="text-slate-500">Kenaikan Kelas Siswa</span>
                    <span className={shouldPromoteStudents ? 'text-emerald-600 font-black' : 'text-slate-400'}>
                      {shouldPromoteStudents ? 'Ya (7→8, 8→9, 9→Lulus)' : 'Tidak'}
                    </span>
                  </div>
                  {shouldPromoteStudents && (
                    <div className="flex justify-between items-center font-bold py-1 border-b border-dashed border-slate-200 dark:border-slate-800">
                      <span className="text-slate-500">Kelas Lulus (Ke ALUMNI)</span>
                      <span className="text-amber-600 dark:text-amber-400 font-black text-right">
                        {grade9ClassesList.map(c => c.name).join(', ') || 'Kelas 9'} ({grade9StudentsCount} Siswa)
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold py-1 border-b border-dashed border-slate-200 dark:border-slate-800">
                    <span className="text-slate-500">Hapus Data Absensi</span>
                    <span className={clearAttendance ? 'text-rose-600 font-black' : 'text-slate-400'}>
                      {clearAttendance ? 'Ya (Hapus Total)' : 'Tidak'}
                    </span>
                  </div>
                  <div className="flex justify-between font-bold py-1 border-b border-dashed border-slate-200 dark:border-slate-800">
                    <span className="text-slate-500">Hapus Semua Nilai</span>
                    <span className={clearGrades ? 'text-rose-600 font-black' : 'text-slate-400'}>
                      {clearGrades ? 'Ya (Hapus Total)' : 'Tidak'}
                    </span>
                  </div>
                  <div className="flex justify-between font-bold py-1 border-b border-dashed border-slate-200 dark:border-slate-800">
                    <span className="text-slate-500">Arsip Agenda Belajar</span>
                    <span className={clearAgendas ? 'text-indigo-600 dark:text-indigo-400 font-black' : 'text-slate-400'}>
                      {clearAgendas ? 'Ya (Arsipkan)' : 'Tidak'}
                    </span>
                  </div>
                  <div className="flex justify-between font-bold py-1">
                    <span className="text-slate-500">Hapus Tugas & Pengumpulan</span>
                    <span className={clearAssignments ? 'text-rose-600 font-black' : 'text-slate-400'}>
                      {clearAssignments ? 'Ya (Hapus Total)' : 'Tidak'}
                    </span>
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
                  <button 
                    type="button"
                    onClick={() => setTransitionStep(1)}
                    className="px-5 py-2.5 bg-slate-100 text-slate-650 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 text-xs font-black uppercase tracking-wider rounded-xl transition-all"
                  >
                    Kembali
                  </button>
                  <button 
                    type="button"
                    onClick={executeTransition}
                    className="px-5 py-3 bg-rose-600 hover:bg-rose-700 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-rose-500/10 active:scale-95 flex items-center gap-1.5"
                  >
                    Konfirmasi & Eksekusi Transisi Akhir 🚀
                  </button>
                </div>
              </div>
            )}

            {/* LOADING STATE */}
            {isTransitioning && (
              <div className="py-12 space-y-8 flex flex-col items-center justify-center animate-fade-in text-center">
                <div className="relative">
                  <div className="w-20 h-20 border-[3px] border-indigo-500/20 border-t-indigo-600 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <RefreshCw className="text-indigo-650 animate-spin" style={{ animationDuration: '4s' }} size={24} />
                  </div>
                </div>
                
                <div className="space-y-1">
                  <h4 className="text-base font-black text-slate-800 dark:text-white">Sedang Memproses Transisi...</h4>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none">
                    Harap tidak menutup atau memuat ulang halaman
                  </p>
                </div>

                <div className="w-full max-w-sm bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden shadow-inner mx-auto">
                  <div 
                    className="h-full bg-indigo-600 transition-all duration-300"
                    style={{ width: `${transitionProgress}%` }}
                  ></div>
                </div>
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">{transitionProgress}% Selesai</span>
              </div>
            )}

            {/* SUCCESS STATE */}
            {transitionStep === 3 && (
              <div className="py-12 space-y-6 flex flex-col items-center justify-center animate-fade-in text-center">
                <div className="w-16 h-16 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/20 animate-bounce mx-auto">
                  <CheckCircle size={32} />
                </div>

                <div className="space-y-2">
                  <h4 className="text-xl font-black text-slate-900 dark:text-white text-center">Transisi Selesai! 🎉</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold leading-relaxed max-w-md mx-auto text-center">
                    Kini sistem telah beralih ke: <strong>Tahun Pelajaran {targetYear} Semester {targetSemester}</strong> secara penuh.
                  </p>
                </div>

                <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl text-emerald-600 dark:text-emerald-400 text-[11px] font-black uppercase tracking-widest leading-normal mx-auto w-fit">
                  Klik tombol di bawah untuk memuat ulang halaman
                </div>

                <button 
                  type="button"
                  onClick={() => window.location.reload()}
                  className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-md flex items-center gap-2 mx-auto"
                >
                  <RefreshCw size={12} /> Muat Ulang Halaman
                </button>
              </div>
            )}

          </div>
        </div>
      )}
      </div>
    </DndContext>
  );
};

export default ScheduleView;
